import { IconWrapper } from '@/components/ui/IconWrapper';
import { CODES, type CodeString } from '@/consts/codes';
import { ROLES, type RoleCode } from '@/consts/roles';
import { ROUTES } from '@/consts/routes';
import type { TranslationKey } from '@/i18n/i18n';
import { Clock, ShoppingCart, Star, Wrench } from 'lucide-react';

export interface MenuItem {
  text: TranslationKey;
  to?: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  badge?: boolean | string;
  code?: CodeString;
  /** If true, this item is hidden from navigation but still used for breadcrumbs */
  hidden?: boolean;
  /** Roles required to see this menu item. If empty/undefined, visible to all. */
  requiredRoles?: RoleCode[];
  /** Explicit sort order within parent. Lower = higher in the list. */
  order?: number;
  /** Custom React component to render instead of the default NavMenuItemRow. */
  component?: React.ComponentType<{ item: MenuItem }>;
  /** If true, renders as a non-collapsible group header (label + line) with children always visible. */
  isGroup?: boolean;
}

/**
 * Native React menu items (PMS and PUR modules).
 * Legacy modules are loaded from JSON config files.
 */
export const nativeMenu: MenuItem[] = [
  {
    text: 'navigation.recent.title',
    icon: <IconWrapper icon={Clock} />,
    children: []
  },
  {
    text: 'navigation.favorites.title',
    icon: <IconWrapper icon={Star} />,
    children: []
  },
  {
    text: 'navigation.pms.title',
    icon: <IconWrapper icon={Wrench} />,
    code: 'pms' as CodeString,
    requiredRoles: [ROLES.PMS_ADMIN],
    children: [
      {
        text: 'navigation.pms.children.defineComponentLibrary',
        to: ROUTES.pms.defineComponentLibrary,
        code: CODES.pms.defineComponentLibrary,
        children: [
          {
            text: 'navigation.pms.children.components',
            to: ROUTES.pms.components,
            code: CODES.pms.components,
            hidden: true,
            children: [
              {
                text: 'navigation.pms.children.componentNew',
                to: ROUTES.pms.componentNew,
                code: CODES.pms.componentNew,
                hidden: true
              },
              {
                text: 'navigation.pms.children.componentEdit',
                to: ROUTES.pms.componentEdit,
                code: CODES.pms.componentEdit,
                hidden: true
              }
            ]
          },
          {
            text: 'navigation.pms.children.spares',
            to: ROUTES.pms.spares,
            code: CODES.pms.spares,
            hidden: true,
            children: [
              {
                text: 'navigation.pms.children.spareNew',
                to: ROUTES.pms.spareNew,
                code: CODES.pms.spareNew,
                hidden: true
              },
              {
                text: 'navigation.pms.children.spareEdit',
                to: ROUTES.pms.spareEdit,
                code: CODES.pms.spareEdit,
                hidden: true
              }
            ]
          },
          {
            text: 'navigation.pms.children.jobs',
            to: ROUTES.pms.jobs,
            code: CODES.pms.jobs,
            hidden: true,
            children: [
              {
                text: 'navigation.pms.children.jobNew',
                to: ROUTES.pms.jobNew,
                code: CODES.pms.jobNew,
                hidden: true
              },
              {
                text: 'navigation.pms.children.jobEdit',
                to: ROUTES.pms.jobEdit,
                code: CODES.pms.jobEdit,
                hidden: true
              }
            ]
          }
        ]
      },
      {
        text: 'navigation.pms.children.vesselComponentLibrary',
        to: ROUTES.pms.vesselComponentLibrary,
        code: CODES.pms.vesselComponentLibrary,
        children: [
          {
            text: 'navigation.pms.children.vessels',
            to: ROUTES.pms.vessels,
            code: CODES.pms.vessels,
            hidden: true,
            children: [
              {
                text: 'navigation.pms.children.vesselAssign',
                to: ROUTES.pms.vesselAssign,
                code: CODES.pms.vesselAssign,
                hidden: true
              },
              {
                text: 'navigation.pms.children.vesselComponentNew',
                to: ROUTES.pms.vesselComponentNew,
                code: CODES.pms.vesselComponentNew,
                hidden: true
              },
              {
                text: 'navigation.pms.children.vesselComponentEdit',
                to: ROUTES.pms.vesselComponentEdit,
                code: CODES.pms.vesselComponentEdit,
                hidden: true
              },
              {
                text: 'navigation.pms.children.vesselSpareNew',
                to: ROUTES.pms.vesselSpareNew,
                code: CODES.pms.vesselSpareNew,
                hidden: true
              },
              {
                text: 'navigation.pms.children.vesselSpareEdit',
                to: ROUTES.pms.vesselSpareEdit,
                code: CODES.pms.vesselSpareEdit,
                hidden: true
              },
              {
                text: 'navigation.pms.children.vesselJobNew',
                to: ROUTES.pms.vesselJobNew,
                code: CODES.pms.vesselJobNew,
                hidden: true
              },
              {
                text: 'navigation.pms.children.vesselJobEdit',
                to: ROUTES.pms.vesselJobEdit,
                code: CODES.pms.vesselJobEdit,
                hidden: true
              }
            ]
          }
        ]
      },
      {
        text: 'navigation.pms.children.mainLibraries',
        to: ROUTES.pms.mainLibraries,
        code: CODES.pms.mainLibraries,
        requiredRoles: [ROLES.PMS_ADMIN_TECHNICAL]
      },
      {
        text: 'navigation.pms.children.fleetJobs',
        to: ROUTES.pms.fleetJobs,
        code: CODES.pms.fleetJobs
      }
    ]
  },
  {
    text: 'navigation.pur.title',
    icon: <IconWrapper icon={ShoppingCart} />,
    code: 'pur' as CodeString,
    requiredRoles: [ROLES.PUR_ADMIN],
    children: [
      {
        text: 'navigation.pur.children.overview',
        to: ROUTES.pur.overview,
        code: CODES.pur.overview,
        children: [
          {
            text: 'navigation.pur.children.requisitions',
            hidden: true,
            to: ROUTES.pur.requisitions,
            code: CODES.pur.requisitions
          },
          {
            text: 'navigation.pur.children.rfq',
            hidden: true,
            to: ROUTES.pur.rfq,
            code: CODES.pur.rfq
          },
          {
            text: 'navigation.pur.children.purchaseOrders',
            hidden: true,
            to: ROUTES.pur.purchaseOrders,
            code: CODES.pur.purchaseOrders
          }
        ]
      },
      {
        text: 'navigation.pur.children.catalogue',
        to: ROUTES.pur.catalogue,
        code: CODES.pur.catalogue
      },
      {
        text: 'navigation.pur.children.vendors',
        to: ROUTES.pur.vendors,
        code: CODES.pur.vendors
      }
    ]
  }
];
